import type { Remark, RemarkCheck } from '@roll1/shared';
import type { TranslationKeys } from '../../../i18n/types.js';

type T = (key: keyof TranslationKeys) => string;

const fill = (text: string, vars: Record<string, string | number>): string =>
  Object.entries(vars).reduce((acc, [k, v]) => acc.split(`{${k}}`).join(String(v)), text);

const FIX_KEY: Record<RemarkCheck, keyof TranslationKeys> = {
  duplicate: 'remarks.fix.duplicate',
  'field-misuse': 'remarks.fix.fieldMisuse',
  'empty-prep': 'remarks.fix.emptyPrep',
  'empty-npc': 'remarks.fix.emptyNpc',
  'broken-link': 'remarks.fix.brokenLink',
};

const BODY_KEY: Record<RemarkCheck, keyof TranslationKeys> = {
  duplicate: 'remarks.b.duplicate',
  'field-misuse': 'remarks.b.fieldMisuse',
  'empty-prep': 'remarks.b.emptyPrep',
  'empty-npc': 'remarks.b.emptyNpc',
  'broken-link': 'remarks.b.brokenLink',
};

/** The title of a remark: the model's own text, or — for a simple one — written here from check + evidence (i18n). */
export function remarkTitle(r: Remark, t: T, kindOf?: (id: string) => 'quest' | 'record'): string {
  if (r.kind !== 'simple' || r.check === undefined) return r.title;
  const first = r.links[0]?.name ?? r.body;
  switch (r.check) {
    case 'duplicate': {
      const original = r.links[1];
      const kind = original !== undefined && kindOf?.(original.id) === 'quest' ? t('remarks.kind.quest') : t('remarks.kind.record');
      return fill(t('remarks.t.duplicate'), { a: first, b: original?.name ?? '', kind });
    }
    case 'empty-prep': return fill(t('remarks.t.emptyPrep'), { beat: first });
    case 'empty-npc': return fill(t('remarks.t.emptyNpc'), { npc: first });
    case 'field-misuse': return fill(t('remarks.t.fieldMisuse'), { quest: first });
    case 'broken-link': return fill(t('remarks.t.brokenLink'), { owner: first });
  }
}

export function remarkBody(r: Remark, t: T): string {
  return r.kind === 'simple' && r.check !== undefined ? t(BODY_KEY[r.check]) : r.body;
}

/** The label of the fix button: simple — by the check (i18n); the model's — its own words, or «Fix it». */
export function fixLabel(r: Remark, t: T): string {
  if (r.kind === 'simple' && r.check !== undefined) return t(FIX_KEY[r.check]);
  return r.fix?.label ?? t('remarks.fix.model');
}

export { fill };
